import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api/apiConfig';
import { campaignErrorMessage } from '../api/adpilotApi';

/** Shared by the AI and manual create pages; account setup never uploads assets. */
export default function UniversalAdsCreativeAssets({ connectionId, creatives = [], selectedIds = [], onSelectionChange,
  onUploaded, onPendingChange, onBusyChange, disabled = false, optional = false, preview = false }) {
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [uploadedName, setUploadedName] = useState('');
  const mounted = useRef(false);
  const operation = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; onPendingChange(false); onBusyChange(false); };
  }, [onPendingChange, onBusyChange]);

  const clearFile = () => {
    setFile(null); setInputKey((value) => value + 1); onPendingChange(false);
  };
  const chooseFile = (event) => {
    const next = event.target.files?.[0];
    setError(''); setUploadedName('');
    if (!next) { clearFile(); return; }
    if (!/\.(mp4|mov)$/i.test(next.name) || !['video/mp4', 'video/quicktime'].includes(next.type)) {
      clearFile(); setError('Choose an MP4 or MOV video.'); return;
    }
    if (!next.size || next.size > 100 * 1024 * 1024) {
      clearFile(); setError('Choose a non-empty video up to 100 MB.'); return;
    }
    setFile(next); onPendingChange(true);
  };
  const upload = async () => {
    if (!file || !connectionId || disabled || operation.current) return;
    operation.current = true; setBusy(true); onBusyChange(true); setError('');
    try {
      const body = new FormData(); body.append('file', file);
      const { data } = preview ? { data: { upload: { creative_id: `demo-video-${Date.now()}`, status: 'completed' } } }
        : await api.post(`/api/v3/adpilot/universal-ads/${connectionId}/creatives/upload`, body);
      if (!data.upload?.creative_id || data.upload.status !== 'completed') throw new Error('The video upload has not been confirmed.');
      if (!mounted.current) return;
      // The upload endpoint already verifies the creative with the platform.
      // Include that returned ID immediately, even if the catalog has not caught up.
      onUploaded({ id: data.upload.creative_id, name: file.name });
      setUploadedName(file.name); clearFile();
    } catch (err) {
      if (mounted.current) setError(campaignErrorMessage(err, 'Could not confirm the video upload. Check it in Universal Ads before trying again.'));
    } finally {
      operation.current = false;
      if (mounted.current) { setBusy(false); onBusyChange(false); }
    }
  };

  return <section className="creative-upload" aria-label="Universal Ads creative assets">
    <div><strong>Creative assets {optional && <span>Optional</span>}</strong>
      <p>Choose existing videos or upload an MP4 or MOV up to 100 MB. Uploaded videos are selected for this campaign.</p>
      {optional && <p>Without a selected video, AdPilot creates a paused campaign that needs video ads before delivery.</p>}
    </div>
    <label className="creative-file-picker"><input key={inputKey} aria-label="Video file" type="file" accept=".mp4,.mov,video/mp4,video/quicktime"
      disabled={disabled || busy} onChange={chooseFile} /><span>{file?.name || 'Choose video file'}</span></label>
    {file && <button type="button" className="btn btn-secondary" disabled={disabled || busy} onClick={upload}>
      {busy ? <><span className="spinner" aria-hidden="true" />Uploading…</> : 'Upload video'}</button>}
    {file && <div className="creative-pending" role="status"><p>{busy ? 'Uploading your video…' : 'Upload or remove this file before building your draft.'}</p>
      <button type="button" className="text-button" disabled={disabled || busy} onClick={() => { clearFile(); setError(''); }}>Remove selected file</button></div>}
    {error && <div className="notice notice-error creative-upload-error" role="alert">{error}</div>}
    {uploadedName && <div className="creative-uploaded" role="status"><span aria-hidden="true">✓</span><div><strong>{uploadedName}</strong><small>Uploaded to this ad account. Review the selected videos below.</small></div></div>}
    <fieldset className="universal-creative-options" disabled={disabled || busy}>
      <legend>Select video creatives</legend>
      {creatives.length > 0 ? <div className="campaign-grid">{creatives.map((creative) => <label className="campaign-option" key={creative.id}>
        <input type="checkbox" checked={selectedIds.includes(creative.id)} onChange={(event) => onSelectionChange(event.target.checked
          ? [...new Set([...selectedIds, creative.id])] : selectedIds.filter((id) => id !== creative.id))} />
        <span className="campaign-option-copy"><strong>{creative.name || 'Untitled video'}</strong><small>{creative.id}</small></span>
      </label>)}</div> : <p>No video creatives yet. Upload a video{optional ? ' or continue without one.' : ' to continue.'}</p>}
    </fieldset>
  </section>;
}
